// Download engine: authorized session → validated manifest → bounded-concurrency piece fetches (out of order)
// → per-piece SHA-256 (WebCrypto) → persistent store → whole-file SHA-256 → ready → user-initiated save.
// It publishes real `solred-transfer-ui/0.2-draft` snapshots; it never advances progress by time.
import { createApi } from './api.js';
import { classify, isTransient, MESSAGES, TransferApiError, TransferFailure } from './errors.js';
import { extOf, validateManifest } from './manifest.js';
import { digestHex } from './sha256.js';
import { buildSnapshot, P } from './snapshot.js';
import { chooseStore } from './stores/index.js';
import { createInlineHasher } from './file-hash.js';

const STORAGE_CODES = new Set(['storage', 'storage-write']);
const NATIVE_BEFORE_BYTES = new Set(['storage', 'storage-write', 'unsupported']);
const NATIVE_ON_RETRY = new Set(['owning-unavailable', 'denied', 'missing', 'storage', 'storage-write', 'unsupported', 'version-changed', 'bad-manifest']);
const SESSION_EXPIRED = Object.freeze({ code: 'session-expired', message: 'Your download session expired. Try again to renew it. Verified pieces are kept.' });
const newId = (p) => `${p}-${(globalThis.crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36))}`;
const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isAbort = (e) => e && (e.name === 'AbortError' || e.code === 20);

/**
 * @typedef {object} DownloadOptions
 * @property {ReturnType<typeof createApi>} [api]
 * @property {string} resourceId
 * @property {string} brand  presentation brand put into snapshots
 * @property {number} [concurrency=4]  pieces in flight
 * @property {number} [maxPieceRetries=4]  integrity failures tolerated per piece
 * @property {number} [maxTransientFailures=8]  consecutive network/server failures before giving up
 * @property {(manifest:object, o:object)=>Promise<{store:object,candidates:number[]}>} [chooseStore]
 * @property {number} [memoryLimit]
 * @property {() => {update:Function,digest:Function,close:Function}} [fileHasher]
 * @property {(snap:object)=>void} [onSnapshot]
 * @property {(ms:number)=>Promise<void>} [sleep]
 * @property {number} [snapshotIntervalMs=120]
 * @property {object} [peers]  public P2P network (see src/sdk/peers/webtorrent.js `createPeerNetwork`). Used only for
 *   explicitly public resources whose manifest carries `torrent`, when the effective policy enables hybrid + public
 *   sharing AND the visitor opts in. Every peer byte goes through the same SHA-256 check before it counts or is stored.
 * @property {(url:string, name:string)=>void} [nativeNavigate]  hands the signed native URL to the browser (tests)
 * @property {string} [nativeFallbackUrl]  owning site's own authorized download URL (validated same-origin by the caller)
 * @property {string} [saveName]  display/save filename alias from the owning site (bytes and hashes are unchanged)
 * @property {string} [expectManifestResourceId]  pin the manifest identity (owning contexts address a grant resource)
 * @property {boolean} [autoNativeFallback=true]  use the native URL when local storage cannot hold the file
 * @property {boolean} [nativeFallbackAllowed]  `false` (managed network refusal, network-management-v1) forbids every
 *   native navigation for this engine's lifetime: storage fallback, native-only start and retry. A session whose policy
 *   has `managedNetworkEnabled: true` forbids it the same way. Sticky: a later unmanaged session never re-enables it.
 * @property {{balancedPeerWaitMs?:number, offloadPeerWaitMs?:number, offloadDiscoveryMs?:number}} [deadlines]
 */

/** Finite waits for peer availability per selection objective (Addendum B). HTTPS always takes over afterwards. */
/** Largest single verified read (one playback segment). */
export const MAX_READ = 32 * 1024 * 1024;
export const OBJECTIVE_DEADLINES = Object.freeze({ balancedPeerWaitMs: 3000, offloadPeerWaitMs: 20_000, offloadDiscoveryMs: 6000, joinWaitMs: 3000 });
const OBJECTIVES = new Set(['balanced', 'origin-offload', 'latency']);

/** Only a same-origin path under the API's session file route is accepted as the native URL. */
function safeNativeUrl(u) {
  if (typeof u !== 'string' || !/^\/transfer-api\/v1\/sessions\/[A-Za-z0-9_-]{1,128}\/file\?ticket=[A-Za-z0-9._~%-]{1,2048}$/.test(u)) return null;
  return u;
}

function defaultNativeNavigate(url, name) {
  const a = document.createElement('a');
  a.href = url; a.download = name || ''; a.rel = 'noreferrer'; a.referrerPolicy = 'no-referrer'; a.style.display = 'none';
  document.body.appendChild(a); a.click(); a.remove();
}

/** @param {DownloadOptions} o */
export function createDownload(o) {
  const api = o.api ?? createApi();
  const sleep = o.sleep ?? defaultSleep;
  const concurrency = Math.max(1, Math.min(16, o.concurrency ?? 4));
  const maxPieceRetries = o.maxPieceRetries ?? 4;
  const maxTransient = o.maxTransientFailures ?? 8;
  const pickStore = o.chooseStore ?? chooseStore;
  const hasherFactory = o.fileHasher ?? createInlineHasher;
  const interval = o.snapshotIntervalMs ?? 120;
  const transferId = o.transferId ?? newId('dl');
  const listeners = new Set(o.onSnapshot ? [o.onSnapshot] : []);
  const deadlines = { ...OBJECTIVE_DEADLINES, ...(o.deadlines || {}) };

  let password = null;
  let session = null; // { sessionId, token, expiresAt }
  let policy = {};
  let manifest = null;
  let store = null;
  let status = 'idle';
  let error = null;
  let sequence = 0;
  let gen = 0; // bumps on pause/cancel so late results from aborted requests are ignored
  let states = null, sources = null, retries = null;
  let queue = [];
  const inflight = new Map(); // index → { ctrl, promise }
  let verifiedBytes = 0, verifiedChunks = 0, receivedBytes = 0;
  let transientFailures = 0, useRange = false, waitingTimers = 0;
  let renewing = null;
  let saveState = 'none';
  let sharingWanted = false; // visitor consent: default off, never persisted
  let swarm = null, joining = null, joinStartedAt = 0, swarmJoinedAt = 0, peerError = null, peerRejected = 0, peerVerified = 0;
  let swarmEpoch = 0; // bumps whenever a swarm is left; peer writes of an old epoch never count
  const writes = new Set(); // every pending piece verification/store write (HTTPS or peer), settled before a store is deleted
  const pieceLocks = new Map(); // one writer per piece index at a time (HTTPS vs peer duplicate races)
  const deferSince = new Map();
  let deferTimer = 0;
  let nativeDownloadUrl = null;
  let nativeOnly = false;
  let nativeForbidden = o.nativeFallbackAllowed === false; // sticky for the engine lifetime once set
  const saveName = () => (typeof o.saveName === 'string' && o.saveName.trim() ? o.saveName.trim().slice(0, 255) : manifest ? manifest.name : '');
  let startedEventSent = false;
  let destroyed = false;
  let flushTimer = 0;
  const meter = { lastAt: 0, lastBytes: 0, rate: null };
  const verifiedListeners = new Set(); // progressive playback: told the index of each newly verified piece
  let priorityFrom = 0; // queue order starts here (playback position); ascending, then wraps
  let objectUrl = null;

  // ---------- snapshots ----------
  const lengthOf = (i) => manifest.pieces[i].length;
  const hybridEnabled = () => policy.enabled === true;
  const sharingEligible = () => !!(o.peers && manifest && manifest.torrent && hybridEnabled() && policy.publicPeerSharing === true);
  const objective = () => (OBJECTIVES.has(policy.selectionObjective) ? policy.selectionObjective : 'balanced');
  const peerStats = () => (swarm ? swarm.stats() : null);

  function sharingSnapshot() {
    if (!sharingEligible()) return null;
    const st = peerStats() || {};
    const net = o.peers.stats ? o.peers.stats() : {};
    const enabled = sharingWanted;
    const rate = enabled ? net.uploadBytesPerSecond ?? st.uploadBytesPerSecond ?? 0 : 0;
    const limit = net.uploadLimitBytesPerSecond ?? policy.peerUploadLimitBytesPerSecond ?? policy.uploadLimitBytesPerSecond ?? null;
    return {
      enabled,
      status: !enabled ? 'off' : peerError || st.blocked ? 'blocked' : limit && rate >= limit * 0.9 ? 'limited' : rate > 0 ? 'active' : 'idle',
      uploadBytesPerSecond: rate,
      uploadedBytes: enabled ? st.uploadedBytes ?? 0 : null,
      limitBytesPerSecond: limit,
      activeFileCount: enabled ? net.activeSwarms ?? (swarm ? 1 : 0) : 0,
      verifiedFromPeers: peerVerified,
      rejectedFromPeers: peerRejected,
      message: enabled && peerError ? peerError : null,
    };
  }

  function snapshot() {
    sequence++;
    const moving = status === 'downloading' || status === 'retrying';
    const size = manifest ? manifest.sizeBytes : null;
    const rate = moving ? meter.rate : null;
    const ps = peerStats();
    return buildSnapshot({
      brand: o.brand,
      transferId,
      sequence,
      direction: 'download',
      mode: sharingEligible() && swarm ? 'hybrid' : 'https',
      status,
      file: { id: manifest ? manifest.resourceId : o.resourceId, name: manifest ? saveName() : '', type: manifest ? extOf(manifest.name) || null : null, sizeBytes: size },
      states,
      sources,
      lengthOf,
      verifiedBytes,
      verifiedChunks,
      receivedBytes,
      metrics: {
        downloadBytesPerSecond: rate,
        etaSeconds: rate && size ? Math.max(0, (size - verifiedBytes) / rate) : null,
        activePeerSources: ps ? ps.activePeers || 0 : 0,
        usingOrigin: !ps || inflight.size > 0 || !moving,
        peerIds: ps && Array.isArray(ps.peerIds) ? ps.peerIds : undefined,
        peerDownloadBytesPerSecond: ps ? ps.downloadBytesPerSecond ?? null : null,
      },
      sharing: sharingSnapshot(),
      sharingEligible: sharingEligible(),
      storage: store ? { mode: store.kind === 'opfs' ? 'opfs' : 'unknown', resumable: !!store.resumable, saveState, requiresUserGesture: true } : null,
      error,
    });
  }

  function flush() {
    flushTimer = 0;
    if (destroyed || !manifest) return;
    updateMeter();
    const s = snapshot();
    for (const fn of listeners) { try { fn(s); } catch (e) { console.error(e); } }
  }

  function emit(force = false) {
    if (destroyed) return;
    if (force) { if (flushTimer) clearTimeout(flushTimer); flush(); return; }
    if (!flushTimer) flushTimer = setTimeout(flush, interval);
  }

  function setStatus(s, err = null) {
    status = s;
    error = err;
    emit(true);
  }

  function updateMeter() {
    const t = Date.now();
    if (!meter.lastAt) { meter.lastAt = t; meter.lastBytes = verifiedBytes; return; }
    const dt = (t - meter.lastAt) / 1000;
    if (dt < 0.25) return;
    const inst = Math.max(0, (verifiedBytes - meter.lastBytes) / dt);
    meter.rate = meter.rate == null ? inst : meter.rate * 0.7 + inst * 0.3;
    meter.lastAt = t; meter.lastBytes = verifiedBytes;
  }

  // ---------- session ----------
  function acceptSession(r) {
    if (!r || typeof r.sessionId !== 'string' || typeof r.token !== 'string') throw new TransferFailure('bad-manifest', MESSAGES['bad-manifest']);
    const m = validateManifest(r.manifest);
    // Owning contexts address a grant resource (e.g. `lolaup-entry-5`) whose manifest carries the transfer resource
    // id; the first accepted manifest pins it, and every renewal must return the same identity.
    const expected = o.expectManifestResourceId ?? (o.api && o.api.owning ? manifest && manifest.resourceId : o.resourceId);
    if (expected != null && m.resourceId !== expected) throw new TransferFailure('bad-manifest', MESSAGES['bad-manifest']);
    if (manifest && (m.version !== manifest.version || m.sha256 !== manifest.sha256)) throw new TransferFailure('version-changed', MESSAGES['version-changed']);
    session = { sessionId: r.sessionId, token: r.token, expiresAt: r.expiresAt ? Date.parse(r.expiresAt) : null, issuedAt: Date.now() };
    nativeDownloadUrl = safeNativeUrl(r.nativeDownloadUrl);
    policy = r.policy && typeof r.policy === 'object' ? r.policy : {};
    if (policy.managedNetworkEnabled === true) nativeForbidden = true;
    if (!manifest) {
      manifest = m;
      states = new Uint8Array(m.pieces.length);
      sources = new Uint8Array(m.pieces.length);
      retries = new Uint8Array(m.pieces.length);
    }
  }

  async function renewSession() {
    if (!renewing) {
      renewing = (async () => {
        try { acceptSession(await api.createSession({ resourceId: o.resourceId, password })); }
        finally { renewing = null; }
      })();
    }
    return renewing;
  }

  async function ensureSession() {
    if (!session || (session.expiresAt && session.expiresAt - Date.now() < 15_000)) await renewSession();
  }

  /**
   * Ask the server for access. Returns the gate result; on success the engine is `idle` with the file known.
   * @returns {Promise<{state:'allowed'|'password-required'|'denied'|'missing'|'expired', passwordError?:boolean, manifest?:object, policy?:object}>}
   */
  async function open(pw) {
    if (pw != null) password = pw;
    try {
      acceptSession(await api.createSession({ resourceId: o.resourceId, password }));
    } catch (e) {
      const k = e instanceof TransferFailure ? e.code : classify(e);
      if (k === 'password-required' || k === 'unauthorized') return { state: 'password-required', passwordError: false };
      if (k === 'password-invalid') { password = null; return { state: 'password-required', passwordError: true }; }
      if (k === 'denied' || k === 'missing' || k === 'expired') return { state: k };
      throw e;
    }
    emit(true);
    return { state: 'allowed', manifest, policy };
  }

  // ---------- scheduling ----------
  /**
   * Objective-aware source choice for one missing piece (Addendum B):
   *  - latency: HTTPS now; peers race in parallel and the first verified copy wins.
   *  - balanced: if an unchoked peer has the piece, give peers a short bounded window, else HTTPS now.
   *  - origin-offload: wait longer for peers that have it, and briefly for peers to appear at all; still finite.
   */
  function deferToPeers(i, now) {
    if (objective() === 'latency') return false;
    // origin-offload: an opted-in swarm that is still joining (metadata, tracker, signalling) gets the same bounded
    // discovery window as a joined swarm without peers — under load the join can outlast `joinWaitMs`.
    if (!swarm) return objective() === 'origin-offload' && !!joining && now - joinStartedAt < deadlines.offloadDiscoveryMs;
    if (!deferSince.has(i)) deferSince.set(i, now);
    const waited = now - deferSince.get(i);
    if (swarm.has(i)) return waited < (objective() === 'origin-offload' ? deadlines.offloadPeerWaitMs : deadlines.balancedPeerWaitMs);
    // origin-offload: during the bounded discovery window, wait whether no peer is connected yet or connected peers are
    // still handshaking (not unchoked / bitfield not received) — otherwise their verified pieces are fetched twice.
    if (objective() === 'origin-offload') return now - swarmJoinedAt < deadlines.offloadDiscoveryMs;
    return false;
  }

  function nextIndex() {
    const now = Date.now(), held = [];
    let pick = -1;
    while (queue.length) {
      const i = queue.shift();
      if (states[i] !== P.PENDING || inflight.has(i)) continue;
      if (deferToPeers(i, now)) { held.push(i); continue; }
      pick = i; break;
    }
    if (held.length) { queue.unshift(...held); scheduleDeferredPump(); }
    return pick;
  }

  function scheduleDeferredPump() {
    if (deferTimer) return;
    deferTimer = setTimeout(() => { deferTimer = 0; pump(); emit(); }, 250);
  }

  function anyRetrying() {
    for (const [i] of inflight) if (retries[i]) return true;
    return queue.some((i) => states[i] === P.RETRYING) || states.includes(P.RETRYING);
  }

  function refreshRunningStatus() {
    if (status !== 'downloading' && status !== 'retrying' && status !== 'reconnecting') return;
    const next = anyRetrying() ? 'retrying' : inflight.size === 0 && waitingTimers > 0 ? 'reconnecting' : 'downloading';
    if (next !== status) setStatus(next);
  }

  const running = () => status === 'downloading' || status === 'retrying' || status === 'reconnecting';

  function pump() {
    if (!running() || destroyed) return;
    while (inflight.size < concurrency) {
      const i = nextIndex();
      if (i < 0) break;
      launch(i);
    }
    if (inflight.size === 0 && waitingTimers === 0 && verifiedChunks === states.length) void verifyWhole();
  }

  function afterVerified(i) {
    deferSince.delete(i);
    if (swarm) swarm.markVerified(i); // a verified HTTPS piece is immediately available to peers
    notifyVerified(i);
  }

  function notifyVerified(i) {
    for (const fn of verifiedListeners) { try { fn(i); } catch { /* a listener never breaks the engine */ } }
  }

  /** Order pending pieces from `priorityFrom` upwards, then the rest. Objectives and deadlines are unchanged. */
  function orderQueue() {
    const n = states.length, from = Math.min(Math.max(0, priorityFrom), Math.max(0, n - 1));
    const key = (i) => (i >= from ? i - from : i + n);
    queue = [...new Set(queue)].sort((a, b) => key(a) - key(b));
  }

  function requeueLater(i, ms, myGen) {
    waitingTimers++;
    refreshRunningStatus();
    sleep(ms).then(() => {
      waitingTimers--;
      if (myGen !== gen) return;
      if (states[i] === P.RETRYING) states[i] = P.PENDING;
      if (states[i] === P.PENDING) queue.push(i);
      refreshRunningStatus();
      emit();
      pump();
    });
  }

  async function fetchPiece(i, signal, viaRange) {
    await ensureSession();
    const p = manifest.pieces[i];
    if (viaRange || p.length === 0) return p.length === 0 ? new Uint8Array(0) : api.range(session, p.offset, p.offset + p.length - 1, signal);
    return api.piece(session, i, signal);
  }

  function track(promise) {
    writes.add(promise);
    promise.then(() => writes.delete(promise), () => writes.delete(promise));
    return promise;
  }

  /** Serialize work on one piece index so two sources can never both count it. */
  function withPiece(i, fn) {
    const prev = pieceLocks.get(i) || Promise.resolve();
    const run = prev.then(fn, fn);
    const tail = run.then(() => {}, () => {});
    pieceLocks.set(i, tail);
    tail.then(() => { if (pieceLocks.get(i) === tail) pieceLocks.delete(i); });
    return run;
  }

  /** Bytes fetched over HTTPS: verified and stored under the piece lock, counted only for the generation that asked. */
  function accept(i, bytes, myGen, sourceSlot) {
    const myStore = store;
    return track(withPiece(i, () => acceptLocked(i, bytes, myGen, sourceSlot, myStore)));
  }

  async function acceptLocked(i, bytes, myGen, sourceSlot, myStore) {
    const p = manifest.pieces[i];
    if (myGen !== gen || store !== myStore || destroyed) return 'stale';
    if (states[i] === P.VERIFIED) return 'duplicate';
    states[i] = P.RECEIVED; sources[i] = sourceSlot; receivedBytes += p.length; emit();
    const ok = bytes.length === p.length && (await digestHex(bytes)) === p.sha256;
    if (myGen !== gen) { if (states[i] === P.RECEIVED) { states[i] = P.PENDING; receivedBytes -= p.length; } return 'stale'; }
    if (states[i] === P.VERIFIED) { receivedBytes -= p.length; return 'duplicate'; }
    if (!ok) { receivedBytes -= p.length; return 'corrupt'; }
    await myStore.put(i, bytes);
    if (myGen !== gen || store !== myStore || destroyed) { if (states[i] === P.RECEIVED) states[i] = P.PENDING; receivedBytes -= p.length; return 'stale'; }
    states[i] = P.VERIFIED; verifiedBytes += p.length; verifiedChunks++;
    return 'verified';
  }

  /**
   * Bytes offered by a peer (WebTorrent store bridge). Already SHA1-checked by WebTorrent against the torrent; here
   * they must also match the manifest SHA-256 before they count or touch storage. Never trusted otherwise.
   */
  const PEER_IDLE = new Set(['cancelled', 'idle', 'pausing', 'paused', 'error', 'expired', 'storage-blocked', 'unsupported', 'offline']);

  function offerFromPeer(i, bytes, slot, epoch) {
    if (!manifest || !store || !states || !Number.isInteger(i) || i < 0 || i >= states.length) return Promise.resolve('stale');
    const myGen = gen, myStore = store;
    // Owned by the generation, swarm epoch and store that were current when the bytes arrived; re-checked after every await.
    const owned = () => !destroyed && myGen === gen && epoch === swarmEpoch && store === myStore && !PEER_IDLE.has(status);
    if (!owned()) return Promise.resolve('stale');
    return track(withPiece(i, async () => {
      if (!owned()) return 'stale';
      if (states[i] === P.VERIFIED) return 'duplicate';
      const p = manifest.pieces[i];
      const ok = bytes.length === p.length && (await digestHex(bytes)) === p.sha256;
      if (!owned()) return 'stale';
      if (!ok) { peerRejected++; emit(); return 'corrupt'; }
      if (states[i] === P.VERIFIED) return 'duplicate';
      await myStore.put(i, bytes);
      if (!owned()) return 'stale';
      if (states[i] === P.VERIFIED) return 'duplicate';
      return countPeerPiece(i, p, slot);
    }));
  }

  function countPeerPiece(i, p, slot) {
    states[i] = P.VERIFIED; sources[i] = 2 + Math.max(0, Math.min(250, slot | 0)); verifiedBytes += p.length; receivedBytes += p.length; verifiedChunks++; peerVerified++;
    deferSince.delete(i);
    const inf = inflight.get(i);
    if (inf && objective() !== 'latency') inf.ctrl.abort(); // HTTPS copy no longer needed
    notifyVerified(i);
    emit();
    if (running()) pump();
    return 'verified';
  }

  /** Store bridge for one swarm epoch: a left swarm's late callbacks can neither count nor read through it. */
  function makePeerBridge(epoch) {
    return {
      offer: (i, bytes, slot) => offerFromPeer(i, bytes, slot, epoch),
      get: (i) => (epoch === swarmEpoch && states && states[i] === P.VERIFIED && store ? store.get(i) : Promise.resolve(null)),
      has: (i) => !!(epoch === swarmEpoch && states && states[i] === P.VERIFIED),
      verifiedIndices: () => { const out = []; if (states) for (let i = 0; i < states.length; i++) if (states[i] === P.VERIFIED) out.push(i); return out; },
    };
  }

  async function joinSwarm() {
    if (swarm || joining || !sharingWanted || !sharingEligible() || !store) return;
    joinStartedAt = Date.now();
    joining = (async () => {
      try {
        await ensureSession();
        const epoch = swarmEpoch;
        const s = await o.peers.join({
          api, session, manifest, policy, bridge: makePeerBridge(epoch), onChange: () => emit(),
          onFail: (err) => {
            if (epoch !== swarmEpoch) return; // an already-left swarm
            swarm = null; swarmEpoch++; deferSince.clear();
            // A write refused because the transfer paused/cancelled is not a peer fault: rejoin later quietly.
            if (!(err && err.solredReason === 'stale')) peerError = 'Sharing stopped: a peer sent data that did not match this file. The download continues from the server.';
            emit(true); if (running()) pump();
          },
        });
        if (!sharingWanted || destroyed || epoch !== swarmEpoch) { s.leave(); return; }
        swarm = s; swarmJoinedAt = Date.now(); peerError = null;
        if (status === 'paused') swarm.pause();
      } catch (e) {
        peerError = (e && e.message) || 'Peer sharing is not available right now.';
      } finally { joining = null; emit(true); if (running()) pump(); }
    })();
    return joining;
  }

  function leaveSwarm() {
    const s = swarm; swarm = null; deferSince.clear();
    swarmEpoch++; // late peer writes of this swarm become stale
    if (s) s.leave(); // stops uploading immediately
  }

  function launch(i) {
    const myGen = gen;
    const entry = { ctrl: new AbortController(), viaRange: useRange, promise: null };
    const release = () => { if (inflight.get(i) === entry) inflight.delete(i); };
    states[i] = P.RECEIVING; sources[i] = 1;
    inflight.set(i, entry);
    entry.promise = (async () => {
      try {
        const bytes = await fetchPiece(i, entry.ctrl.signal, entry.viaRange);
        if (myGen !== gen) return;
        const r = await accept(i, bytes, myGen, 1);
        release();
        if (r === 'verified') { transientFailures = 0; retries[i] = 0; afterVerified(i); }
        else if (r === 'corrupt') onCorrupt(i, myGen);
      } catch (e) {
        release(); // before any requeue, so the index can be scheduled again immediately
        await onFetchError(i, e, myGen, entry.viaRange);
      } finally {
        release();
        if (myGen === gen) { refreshRunningStatus(); emit(); pump(); }
      }
    })();
  }

  function onCorrupt(i, myGen) {
    retries[i]++;
    if (retries[i] > maxPieceRetries) { states[i] = P.PENDING; return fail('piece-integrity'); }
    states[i] = P.RETRYING;
    setStatus('retrying');
    requeueLater(i, Math.min(8000, 250 * 2 ** (retries[i] - 1)), myGen);
  }

  async function onFetchError(i, e, myGen, viaRange) {
    if (states[i] !== P.VERIFIED) states[i] = P.PENDING;
    if (myGen !== gen || isAbort(e)) { if (myGen === gen && states[i] === P.PENDING) queue.push(i); return; }
    if (e instanceof TransferFailure) return fail(e.code, e);
    const k = classify(e);
    if (k === 'unauthorized' || k === 'expired') {
      try { await renewSession(); } catch (re) {
        const rk = re instanceof TransferFailure ? re.code : classify(re);
        if (rk === 'version-changed') return fail('version-changed');
        if (re instanceof TransferFailure) return fail(rk, re); // e.g. the owning site refused a renewal: no retry loop
        if (rk === 'denied' || rk === 'missing') return fail(rk);
        if (isTransient(rk)) { transientFailures++; if (transientFailures > maxTransient) return fail(rk === 'network' ? 'network' : 'server'); return requeueLater(i, backoff(re), myGen); }
        abortAll();
        return setStatus('expired', SESSION_EXPIRED);
      }
      queue.unshift(i);
      return;
    }
    if (k === 'missing' && !viaRange && e instanceof TransferApiError) { useRange = true; queue.unshift(i); return; } // pieces route absent → HTTPS Range
    if (k === 'missing' || k === 'denied') return fail(k);
    if (isTransient(k)) {
      if (k === 'network' && typeof navigator !== 'undefined' && navigator.onLine === false) { queue.unshift(i); return goOffline(); }
      transientFailures++;
      if (transientFailures > maxTransient) return fail(k === 'network' ? 'network' : 'server');
      return requeueLater(i, backoff(e), myGen);
    }
    return fail('server');
  }

  function backoff(e) {
    if (e && e.retryAfterMs != null) return e.retryAfterMs;
    return Math.min(15_000, 400 * 2 ** Math.min(6, transientFailures - 1)) + Math.floor(Math.random() * 200);
  }

  function abortAll() {
    gen++;
    for (const [, f] of inflight) f.ctrl.abort();
  }

  function fail(code, cause) {
    abortAll();
    // Local storage cannot hold the file before any byte arrived: hand the download to the browser natively
    // (explicit signed/owning URL, never a RAM copy of a large file).
    if (NATIVE_BEFORE_BYTES.has(code) && verifiedBytes === 0 && hasNativeUrl() && o.autoNativeFallback !== false) { void nativeDownload('native-storage'); return; }
    let msg = cause && cause.message && cause instanceof TransferFailure ? cause.message : MESSAGES[code] || MESSAGES.server;
    if (NATIVE_ON_RETRY.has(code) && hasNativeUrl()) msg = `${msg} ${MESSAGES['native-on-retry']}`;
    if (STORAGE_CODES.has(code)) return setStatus('storage-blocked', { code, message: msg });
    if (code === 'unsupported') return setStatus('unsupported', { code, message: msg });
    setStatus('error', { code, message: msg });
  }

  function goOffline() {
    abortAll();
    setStatus('offline');
  }

  function rebuildQueue() {
    queue = [];
    for (let i = 0; i < states.length; i++) if (states[i] !== P.VERIFIED) { states[i] = P.PENDING; queue.push(i); }
    if (priorityFrom > 0) orderQueue();
  }

  /** Wait for HTTPS requests and every pending verification/store write (HTTPS or peer) to finish. */
  async function settleInflight() {
    for (let round = 0; round < 8 && (inflight.size || writes.size); round++) {
      await Promise.allSettled([...[...inflight.values()].map((f) => f.promise), ...writes]);
    }
  }

  /** Unbounded variant before ownership ends: after abortAll()/destroy() no new fetch or write starts, so this ends. */
  async function drainWrites() {
    while (inflight.size || writes.size) await Promise.allSettled([...[...inflight.values()].map((f) => f.promise), ...writes]);
  }

  // ---------- verification ----------
  async function verifyWhole() {
    if (status === 'verifying' || status === 'finalizing' || status === 'ready') return;
    const myGen = gen;
    setStatus('verifying');
    const h = hasherFactory();
    try {
      for (let i = 0; i < states.length; i++) {
        const b = await store.get(i);
        if (myGen !== gen) return;
        if (!b || b.length !== manifest.pieces[i].length) throw new TransferFailure('file-integrity', MESSAGES['file-integrity']);
        await h.update(b);
      }
      const hex = await h.digest();
      if (myGen !== gen) return;
      if (hex !== manifest.sha256) {
        const st = store;
        store = null; // removed (and its lease released): the next start picks a fresh store
        await st.remove();
        resetProgress();
        return fail('file-integrity');
      }
      setStatus('finalizing');
      await store.complete();
      if (myGen !== gen) return;
      sendEvent('download.verified');
      setStatus('ready');
    } catch (e) {
      if (myGen !== gen) return;
      if (e instanceof TransferFailure) return fail(e.code, e);
      fail('server');
    } finally { h.close(); }
  }

  function resetCounters() { verifiedBytes = 0; verifiedChunks = 0; receivedBytes = 0; queue = []; }

  function resetProgress() {
    states.fill(P.PENDING); sources.fill(0); retries.fill(0);
    resetCounters();
  }

  function sendEvent(type) {
    if (!session || !api.event) return;
    api.event(session, { eventId: newId('ev'), type, at: new Date().toISOString() }).catch(() => {});
  }

  // ---------- public controls ----------
  async function begin(fromStatus) {
    const myGen = ++gen;
    setStatus(fromStatus);
    try {
      await ensureSession();
      if (myGen !== gen) return;
      if (!store) {
        const picked = await pickStore(manifest, { policy, memoryLimit: o.memoryLimit });
        if (myGen !== gen) { await picked.store.remove?.(); return; }
        store = picked.store;
        for (const i of picked.candidates) { // resume: only pieces whose bytes still hash correctly count
          const b = await store.get(i);
          if (myGen !== gen) return;
          if (b && b.length === manifest.pieces[i].length && (await digestHex(b)) === manifest.pieces[i].sha256) {
            if (states[i] !== P.VERIFIED) { states[i] = P.VERIFIED; sources[i] = 1; verifiedBytes += b.length; receivedBytes += b.length; verifiedChunks++; }
          } else await store.drop(i);
        }
      }
      if (myGen !== gen) return;
      if (!startedEventSent) { startedEventSent = true; sendEvent('download.started'); }
      rebuildQueue();
      if (swarm) swarm.resume();
      else if (sharingWanted && sharingEligible()) {
        // Give an opted-in swarm a bounded moment to come up so the objective applies from the first piece.
        let waitTimer = 0;
        await Promise.race([joinSwarm(), new Promise((r) => { waitTimer = setTimeout(r, deadlines.joinWaitMs ?? 3000); })]);
        clearTimeout(waitTimer);
        if (myGen !== gen) return;
      }
      transientFailures = 0;
      meter.lastAt = 0; meter.rate = null;
      setStatus('downloading');
      pump();
    } catch (e) {
      if (myGen !== gen) return;
      if (e instanceof TransferFailure) return fail(e.code, e);
      const k = classify(e);
      if (k === 'expired' || k === 'unauthorized') return setStatus('expired', SESSION_EXPIRED);
      if (k === 'denied' || k === 'missing') return fail(k);
      if (k === 'network' && typeof navigator !== 'undefined' && navigator.onLine === false) return goOffline();
      fail(k === 'network' ? 'network' : 'server');
    }
  }

  async function start() {
    if (status !== 'idle' && status !== 'cancelled') return;
    if (!manifest) { const r = await open(); if (r.state !== 'allowed') return r; }
    if (!hybridEnabled() || nativeOnly) return nativeDownload();
    await begin('preparing');
  }

  /**
   * Hybrid disabled for this session (rollout/brand/admin): hand the short-lived signed ticket URL to the browser's
   * own download manager (Addendum A Q4). No fetch, no Blob, no progress we cannot observe.
   */
  async function nativeDownload(reason = 'native-download') {
    if (nativeForbidden) return refuseNative();
    nativeOnly = true; // later starts go straight to the native path: no retry loop through a failing engine
    let url = o.nativeFallbackUrl || null; // owning site: its own already-authorized download (keeps its accounting)
    if (!url) {
      try {
        if (!session || !nativeDownloadUrl || Date.now() - session.issuedAt > 30_000) await renewSession();
      } catch (e) {
        const k = e instanceof TransferFailure ? e.code : classify(e);
        return setStatus('idle', { code: k === 'denied' || k === 'missing' ? k : 'native-unavailable', message: MESSAGES[k] || MESSAGES['native-unavailable'] });
      }
      url = nativeDownloadUrl;
    }
    if (nativeForbidden) return refuseNative(); // the renewed session is managed
    if (!url) return setStatus('idle', { code: 'native-unavailable', message: MESSAGES['native-unavailable'] });
    if (!o.nativeFallbackUrl) sendEvent('download.started');
    const go = o.nativeNavigate ?? defaultNativeNavigate;
    go(url, saveName());
    setStatus('idle', { code: reason, message: MESSAGES[reason] || MESSAGES['native-download'] });
  }

  /** Managed network: the browser never navigates natively; the transfer stays on authorized sessions. */
  function refuseNative() {
    nativeOnly = false;
    setStatus('idle', { code: 'native-unavailable', message: MESSAGES['native-unavailable'] });
  }

  const hasNativeUrl = () => !nativeForbidden && !!(o.nativeFallbackUrl || nativeDownloadUrl);

  async function pause() {
    if (!running() && status !== 'preparing' && status !== 'resuming') return;
    if (swarm) swarm.pause();
    abortAll();
    setStatus('pausing');
    await settleInflight();
    if (status === 'pausing') { rebuildQueue(); setStatus('paused'); }
  }

  async function resume() {
    if (status !== 'paused') return;
    await begin('resuming');
  }

  async function retry() {
    if (!['error', 'offline', 'expired', 'storage-blocked', 'unsupported'].includes(status)) return;
    // Refusals the engine cannot fix by trying again go to the native download instead of looping.
    if (error && NATIVE_ON_RETRY.has(error.code) && hasNativeUrl()) return nativeDownload('native-fallback');
    if (status === 'expired') session = null;
    if (error && (error.code === 'piece-integrity')) retries.fill(0);
    await settleInflight();
    if (error && error.code === 'version-changed') { // never mix pieces of two versions: start over on the new one
      await drainWrites(); // no owned write may outlive the released lease
      if (store) await store.remove().catch(() => {});
      store = null; manifest = null; states = sources = retries = null; session = null;
      resetCounters();
      const r = await open();
      if (r.state !== 'allowed') return r;
      return begin('preparing');
    }
    await begin('resuming');
  }

  async function cancel() {
    if (['idle', 'cancelled', 'ready', 'saving', 'saved'].includes(status)) return;
    abortAll();
    leaveSwarm();
    await drainWrites(); // every late write finishes (and is discarded) before ownership ends and bytes go
    if (store) { const st = store; store = null; await st.remove().catch(() => {}); }
    resetProgress();
    saveState = 'none';
    sendEvent('download.cancelled');
    setStatus('cancelled');
  }

  async function blob() {
    if (!store || !['ready', 'saving', 'saved', 'save-error'].includes(status)) return null;
    return store.blob(manifest.mime);
  }

  /** Object URL of the verified local file (for previews). Revoked on destroy. */
  async function previewUrl() {
    if (objectUrl) return objectUrl;
    const b = await blob();
    if (!b) return null;
    objectUrl = URL.createObjectURL(b);
    return objectUrl;
  }

  /**
   * Save the verified file. Call from a user gesture (click). Uses the File System Access save picker when the
   * browser has it (write confirmed → `saved`); otherwise hands the file to the browser's download manager, which
   * the page cannot confirm, so the status stays `ready` with an explanatory message.
   */
  async function save({ picker = o.savePicker ?? globalThis.showSaveFilePicker } = {}) {
    if (status !== 'ready' && status !== 'save-error' && status !== 'saved') return;
    const prev = status;
    saveState = 'pending';
    setStatus('saving');
    try {
      if (typeof picker === 'function') {
        const handle = await picker({ suggestedName: saveName() });
        const writable = await handle.createWritable();
        const b = await store.blob(manifest.mime);
        await b.stream().pipeTo(writable);
        saveState = 'confirmed';
        sendEvent('download.saved');
        setStatus('saved');
      } else {
        const b = await store.blob(manifest.mime);
        const url = URL.createObjectURL(b);
        const a = document.createElement('a');
        a.href = url; a.download = saveName(); a.rel = 'noopener'; a.style.display = 'none';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        saveState = 'pending';
        setStatus('ready', { code: 'save-handed-off', message: MESSAGES['save-handed-off'] });
      }
    } catch (e) {
      if (isAbort(e)) { saveState = 'none'; return setStatus(prev === 'saved' ? 'saved' : 'ready', { code: 'save-dismissed', message: MESSAGES['save-dismissed'] }); }
      saveState = 'error';
      setStatus('save-error', { code: 'save-failed', message: MESSAGES['save-failed'] });
    }
  }

  // Partial relay target (network-relay-http-v1): the current authorized session (renewals replace it), up to 32
  // still-missing foreground pieces as demand hints, and foreground pressure, which suppresses auxiliary prefill.
  let relayTargetOf = null; // one stable target per engine, so opt-out releases exactly this engine's relay
  function relayTarget() {
    return relayTargetOf || (relayTargetOf = {
      session: () => session,
      getWant: () => { const out = []; if (states) for (let i = 0; i < states.length && out.length < 32; i++) if (states[i] !== P.VERIFIED) out.push(i); return out; },
      isBusy: () => running() || inflight.size > 0,
    });
  }

  /** Explicit visitor consent. Opt-out leaves the swarm at once (no further uploads); HTTPS continues. */
  function toggleSharing() {
    if (!sharingEligible()) return false;
    sharingWanted = !sharingWanted;
    if (sharingWanted) { peerError = null; void joinSwarm(); o.peers.setConsent?.(true, { api, policy, exclude: new Set([o.resourceId]), relay: relayTarget() }); }
    else { leaveSwarm(); o.peers.setConsent?.(false, { relay: relayTarget() }); }
    emit(true);
    return true;
  }

  function onOnline() { if (status === 'offline') void begin('resuming'); }
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);

  function destroy() {
    destroyed = true;
    abortAll();
    if (flushTimer) clearTimeout(flushTimer);
    if (deferTimer) clearTimeout(deferTimer);
    leaveSwarm();
    if (sharingWanted) { sharingWanted = false; o.peers?.setConsent?.(false, { relay: relayTarget() }); }
    const st = store;
    // Keep the key leased until every owned write has settled (no new work starts after `destroyed`).
    if (st && st.release) void drainWrites().then(() => st.release(), () => st.release());
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
    listeners.clear();
    verifiedListeners.clear();
  }

  // ---------- verified reads (progressive playback) ----------
  const pieceAt = (offset) => (manifest && manifest.pieceLength ? Math.floor(offset / manifest.pieceLength) : 0);
  const isVerified = (i) => !!(states && states[i] === P.VERIFIED);
  /**
   * Bytes [offset, offset+length) only if every covering piece is VERIFIED (SHA-256 checked) in the current store;
   * null otherwise. Never returns unverified or partial data. Bounded to one maximum piece length per call.
   */
  async function readVerified(offset, length) {
    if (!manifest || !store || !Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0) return null;
    if (length > MAX_READ || offset + length > manifest.sizeBytes) return null;
    if (length === 0) return new Uint8Array(0);
    const myStore = store, first = pieceAt(offset), last = pieceAt(offset + length - 1);
    for (let i = first; i <= last; i++) if (!isVerified(i)) return null;
    const out = new Uint8Array(length);
    for (let i = first; i <= last; i++) {
      const p = manifest.pieces[i];
      const b = await myStore.get(i);
      if (store !== myStore || !isVerified(i) || !b || b.length !== p.length) return null;
      const a = Math.max(offset, p.offset), z = Math.min(offset + length, p.offset + p.length);
      out.set(b.subarray(a - p.offset, z - p.offset), a - offset);
    }
    return out;
  }

  return {
    transferId,
    open, start, pause, resume, cancel, retry, save, toggleSharing, blob, previewUrl, destroy,
    subscribe(fn) { listeners.add(fn); if (manifest) fn(snapshot()); return () => listeners.delete(fn); },
    snapshot: () => (manifest ? snapshot() : null),
    get status() { return status; },
    get manifest() { return manifest; },
    get policy() { return policy; },
    get sharingEligible() { return sharingEligible(); },
    get hybridEnabled() { return hybridEnabled(); },
    get sharing() { return sharingWanted; },
    // progressive playback hooks
    isVerified,
    pieceAt,
    readVerified,
    /** Prefer pieces from this byte offset onwards (playback position). */
    prioritize(offset) {
      if (!manifest) return;
      priorityFrom = pieceAt(Math.max(0, Math.min(offset, Math.max(0, manifest.sizeBytes - 1))));
      if (states && queue.length) { orderQueue(); if (running()) pump(); }
    },
    onVerified(fn) { verifiedListeners.add(fn); return () => verifiedListeners.delete(fn); },
    /** Dispatch a view action (`{type}`) to the matching control. Unknown actions return false. */
    dispatch(a) {
      switch (a && a.type) {
        case 'start': return start(), true;
        case 'pause': return pause(), true;
        case 'resume': return resume(), true;
        case 'cancel': return cancel(), true;
        case 'retry': return retry(), true;
        case 'save': case 'retry-save': return save(), true;
        case 'toggle-sharing': return toggleSharing();
        default: return false;
      }
    },
  };
}
